import { ApiClient } from '@/lib/apis/clients/ApiClient'
import type { UploadResponse } from '@/lib/apis/types'
import type { UploadAdapter } from '@medmax/api-client/upload'
import { uploadSupportingDocument } from '@medmax/api-client/upload'

export namespace GenericUploadSupportingDocuments {
  export type File = {
    name: string
    type: string
    url: string
  }

  export type Params = {
    document: File
    updateProgress: (number: number) => void
    creationToken?: string
  }

  export async function api(
    client: ApiClient,
    presignedGenUrl: string,
    { document, updateProgress, creationToken }: Params,
  ): Promise<UploadResponse> {
    return uploadSupportingDocument(
      client,
      presignedGenUrl,
      { document, updateProgress, creationToken },
      browserUploadAdapter,
    )
  }

  export function mutation(
    client: ApiClient,
    presignedGenUrl: string,
    mutationKey: Array<string>,
  ) {
    return {
      mutationFn: async (params: Params) =>
        (await api(client, presignedGenUrl, params)).token,
      mutationKey,
    }
  }

  export function mutationWithResponse(
    client: ApiClient,
    presignedGenUrl: string,
    mutationKey: Array<string>,
  ) {
    return {
      mutationFn: async (params: Params) =>
        await api(client, presignedGenUrl, params),
      mutationKey,
    }
  }
}

const browserUploadAdapter: UploadAdapter = {
  async read(document) {
    const fetched = await fetch(document.url)
    return { bytes: await fetched.arrayBuffer() }
  },
  async sha256Base64(bytes) {
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    const hash = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
    return btoa(
      hash
        .match(/\w{2}/g)!
        .map((a) => String.fromCharCode(parseInt(a, 16)))
        .join(''),
    )
  },
  async put({ url, contentType, checksumSha256Base64, bytes, updateProgress }) {
    await new Promise((resolve, reject) => {
      if (typeof XMLHttpRequest === 'undefined') {
        reject(
          new UploadError(
            'XMLHttpRequest is not available in this environment',
            {
              type: 'unsupported',
            },
          ),
        )
        return
      }

      const xhr = new XMLHttpRequest()
      let settled = false
      const blob = new Blob([bytes])

      const cleanup = () => {
        xhr.onload = null
        xhr.onerror = null
        xhr.ontimeout = null
        xhr.onabort = null
        if (xhr.upload) {
          xhr.upload.onerror = null
          xhr.upload.onprogress = null
        }
      }

      const safeReject = (err: UploadError) => {
        cleanup()

        if (!settled) {
          settled = true
          reject(err)
        }
      }

      const safeResolve = () => {
        cleanup()

        if (!settled) {
          settled = true
          resolve(true)
        }
      }

      const safeResponseBody = (): string => {
        switch (xhr.responseType) {
          case 'document':
            return `DOCUMENT: ${xhr.response}`

          case 'arraybuffer': {
            const decoder = new TextDecoder('utf-8')
            const decodedString = decoder.decode(xhr.response)

            return `ARRAYBUFFER: ${decodedString}`
          }
          case 'json':
            return `JSON: ${JSON.stringify(xhr.response)}`
          case 'text':
            return `TEXT: ${xhr.responseText}`
          case 'blob':
            return `BLOB: ${xhr.responseURL}`
          default:
            return `NO RESPONSE BODY`
        }
      }

      try {
        xhr.open('PUT', url, true)
        xhr.setRequestHeader('Content-Type', contentType)
        xhr.setRequestHeader('x-amz-checksum-sha256', checksumSha256Base64)
      } catch (e) {
        safeReject(
          new UploadError('Failed to open XMLHttpRequest', {
            type: 'network',
            cause: e,
          }),
        )
        return
      }

      xhr.onload = () => {
        if (xhr.status === 0) {
          safeReject(
            new UploadError(
              'Request failed (status 0, possibly CORS or network error)',
              {
                type: 'network',
                status: xhr.status,
                responseText: safeResponseBody(),
              },
            ),
          )
          return
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          safeResolve()
        } else {
          safeReject(
            new UploadError(`HTTP error during upload: ${xhr.status}`, {
              type: 'http',
              status: xhr.status,
              responseText: safeResponseBody(),
            }),
          )
        }
      }

      xhr.onerror = () => {
        safeReject(
          new UploadError('Network error during request', {
            type: 'network',
            status: xhr.status,
            responseText: safeResponseBody(),
          }),
        )
      }

      xhr.ontimeout = () => {
        safeReject(
          new UploadError(`Upload timed out after ${xhr.timeout} ms`, {
            type: 'timeout',
            status: xhr.status,
            responseText: safeResponseBody(),
          }),
        )
      }

      xhr.onabort = () => {
        safeReject(
          new UploadError('Upload was aborted', {
            type: 'abort',
            status: xhr.status,
            responseText: safeResponseBody(),
          }),
        )
      }

      if (xhr.upload) {
        xhr.upload.onerror = () => {
          safeReject(
            new UploadError('Upload stream failed while sending data', {
              type: 'upload',
              status: xhr.status,
              responseText: safeResponseBody(),
            }),
          )
        }

        xhr.upload.onprogress = (e) => {
          if (!e.lengthComputable) {
            updateProgress(0)
          } else {
            const fraction = e.total ? e.loaded / e.total : 0
            updateProgress(fraction)
          }
        }
      }

      try {
        xhr.send(blob)
      } catch (e) {
        safeReject(
          new UploadError('Failed to send upload request', {
            type: 'network',
            cause: e,
          }),
        )
      }
    })
  },
}

interface UploadErrorInfo {
  type: 'network' | 'timeout' | 'abort' | 'upload' | 'http' | 'unsupported'
  status?: number
  responseText?: string
  cause?: any
}

export class UploadError extends Error {
  /**
   * @param {string} message
   * @param {{
   *   type: 'network' | 'timeout' | 'abort' | 'upload' | 'http' | 'unsupported',
   *   status?: number,
   *   responseText?: string,
   *   cause?: any
   * }} info
   */
  type: 'network' | 'timeout' | 'abort' | 'upload' | 'http' | 'unsupported'
  status?: number
  responseText?: string
  cause?: any

  constructor(message: string, info: UploadErrorInfo) {
    super(message)
    this.name = 'UploadError'
    this.type = info.type
    this.status = info.status
    this.responseText = info.responseText
    this.cause = info.cause
  }
}
